import { useEffect } from "react";

import { useFormikContext } from "formik";
import { get } from "lodash";
import { useSelector } from "react-redux";

import { withNamespace } from "components/Form/FormikControls";

import { selectCurrentMixedBreedId } from "selectors";

/**
 * Mirror effects between the top-level breed/quantity fields and the
 * advanced-drafting breed1 fields. Currently consumed by the desktop
 * DetailedSaleLotModal; the `namespace` arg keeps it generic so a future
 * mobile sale-lot form can opt in without duplicating the rules.
 *
 * @param namespace - field-path prefix for the sale-lot values within the
 *   Formik form. Pass `undefined` when values live at the top level, or
 *   e.g. `"saleLot"` when they're nested under `values.saleLot`.
 */
export const useAdvancedDraftingMirrorEffects = (namespace?: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values, setFieldValue } = useFormikContext<any>();
  const mixedBreedId = useSelector(selectCurrentMixedBreedId);

  const breedIdPath = withNamespace(namespace, "breed_id");
  const breed1IdPath = withNamespace(
    namespace,
    "draftingAttributes.advancedBreed1Id",
  );
  const breed2IdPath = withNamespace(
    namespace,
    "draftingAttributes.advancedBreed2Id",
  );
  const breed1HdPath = withNamespace(
    namespace,
    "draftingAttributes.advancedBreed1Hd",
  );
  const breed2HdPath = withNamespace(
    namespace,
    "draftingAttributes.advancedBreed2Hd",
  );
  const breed3HdPath = withNamespace(
    namespace,
    "draftingAttributes.advancedBreed3Hd",
  );
  const quantityPath = withNamespace(namespace, "quantity");

  const breedId = get(values, breedIdPath);
  const advancedBreed1Id = get(values, breed1IdPath);
  const advancedBreed2Id = get(values, breed2IdPath);
  const advancedBreed1Hd = get(values, breed1HdPath);
  const advancedBreed2Hd = get(values, breed2HdPath);
  const advancedBreed3Hd = get(values, breed3HdPath);
  const quantity = get(values, quantityPath);

  // When the breed and quantity is set, and the advanced drafting fields are
  // not, set them.
  useEffect(() => {
    if (breedId && advancedBreed1Id === null) {
      setFieldValue(breed1IdPath, breedId, true);
    }
  }, [breedId, advancedBreed1Id, breed1IdPath, setFieldValue]);

  useEffect(() => {
    if (
      quantity &&
      !advancedBreed2Hd &&
      !advancedBreed3Hd &&
      advancedBreed1Hd !== quantity
    ) {
      setFieldValue(breed1HdPath, quantity, true);
    }
  }, [
    quantity,
    advancedBreed1Hd,
    advancedBreed2Hd,
    advancedBreed3Hd,
    breed1HdPath,
    setFieldValue,
  ]);

  // If the advanced breeds 1 and 2 are set, the primary breed should flip to
  // mixed.
  useEffect(() => {
    if (
      mixedBreedId &&
      breedId !== mixedBreedId &&
      !!advancedBreed1Id &&
      !!advancedBreed2Id
    ) {
      setFieldValue(breedIdPath, mixedBreedId, true);
    }
  }, [
    breedId,
    advancedBreed1Id,
    advancedBreed2Id,
    mixedBreedId,
    breedIdPath,
    setFieldValue,
  ]);

  // If breedId is changed and different from advanced breed1 and there is only
  // one advanced breed, change breed1 to the main breed.
  useEffect(() => {
    if (breedId !== advancedBreed1Id && !advancedBreed2Id) {
      setFieldValue(breed1IdPath, breedId, true);
    }
  }, [
    breedId,
    advancedBreed1Id,
    advancedBreed2Id,
    breed1IdPath,
    setFieldValue,
  ]);
};
